// Counts-only device facts (design 3.4 step 1, 7.8). Nothing here identifies a
// person: a device class, whether the page runs inside an app's browser, and
// whether data saving is on. Pure functions over injected values, so tests can
// feed any user agent.

export type DeviceClass = "phone" | "tablet" | "desktop" | "unknown";

export interface DeviceHints {
  userAgent: string;
  /** matchMedia("(pointer: coarse)").matches */
  coarsePointer: boolean;
  /** Smallest screen side in CSS pixels. */
  minScreenSide: number;
  maxTouchPoints: number;
}

export function readDeviceHints(
  win: Window | undefined = typeof window !== "undefined" ? window : undefined,
): DeviceHints {
  if (!win) return { userAgent: "", coarsePointer: false, minScreenSide: 0, maxTouchPoints: 0 };
  let coarsePointer = false;
  try {
    coarsePointer = Boolean(win.matchMedia?.("(pointer: coarse)")?.matches);
  } catch {
    coarsePointer = false;
  }
  return {
    userAgent: win.navigator?.userAgent ?? "",
    coarsePointer,
    minScreenSide: Math.min(win.screen?.width ?? 0, win.screen?.height ?? 0),
    maxTouchPoints: win.navigator?.maxTouchPoints ?? 0,
  };
}

export function deviceClass(hints: DeviceHints): DeviceClass {
  const ua = hints.userAgent;
  if (!ua && !hints.minScreenSide) return "unknown";
  if (/iPad|Tablet|PlayBook|Silk/i.test(ua) || (/Macintosh/.test(ua) && hints.maxTouchPoints > 1)) return "tablet";
  if (/Android/i.test(ua) && !/Mobile/i.test(ua)) return "tablet";
  if (/iPhone|iPod|Android.+Mobile|Mobile Safari|Windows Phone/i.test(ua)) return "phone";
  if (hints.coarsePointer && hints.minScreenSide > 0 && hints.minScreenSide < 600) return "phone";
  if (hints.coarsePointer && hints.minScreenSide >= 600) return "tablet";
  return "desktop";
}

/**
 * Apps whose built-in browser may not allow the mic (design 7.8: UNKNOWN until
 * tested, so the page offers "Open in Safari or Chrome" first). Names are for
 * the counts only.
 */
const IN_APP_PATTERNS: readonly [RegExp, string][] = [
  [/Instagram/i, "instagram"],
  [/FBAN|FBAV|FB_IAB|FBIOS|FB4A/i, "facebook"],
  [/LinkedInApp/i, "linkedin"],
  [/GSA\//i, "google_app"],
  [/musical_ly|BytedanceWebview|TikTok/i, "tiktok"],
  [/Snapchat/i, "snapchat"],
  [/Twitter|TwitterAndroid/i, "x"],
  [/Line\//i, "line"],
  [/Pinterest/i, "pinterest"],
  [/; wv\)/i, "android_webview"],
];

export function inAppBrowser(userAgent: string): string | null {
  for (const [pattern, name] of IN_APP_PATTERNS) if (pattern.test(userAgent)) return name;
  return null;
}

export function isAndroid(userAgent: string): boolean {
  return /Android/i.test(userAgent);
}

export function isIos(userAgent: string): boolean {
  return /iPhone|iPad|iPod/i.test(userAgent) || (/Macintosh/.test(userAgent) && /Mobile/.test(userAgent));
}

/** Data saver on: skip the early 3D download (Safari never reports this; that is fine). */
export function saveDataOn(
  nav: Navigator | undefined = typeof navigator !== "undefined" ? navigator : undefined,
): boolean {
  const connection = (nav as (Navigator & { connection?: { saveData?: boolean } }) | undefined)?.connection;
  return Boolean(connection?.saveData);
}

/** Phones and tablets render the lighter 3D build (the old "phone profile": a coarse pointer). */
export function usesPhoneModel(hints: DeviceHints): boolean {
  return hints.coarsePointer;
}

/**
 * A guess at where Otto's voice comes out, from the output latency the browser
 * reports. Bluetooth adds about 150 to 250 ms (ESTIMATE, research 01 section 6),
 * so a reported output latency over 100 ms is read as Bluetooth, and no extra
 * lip-sync delay is added there (design 7.3). Counts only.
 */
export function outputRouteFromLatency(outputLatencySeconds: number | undefined): "bluetooth" | "unknown" {
  if (typeof outputLatencySeconds !== "number" || !Number.isFinite(outputLatencySeconds)) return "unknown";
  return outputLatencySeconds > 0.1 ? "bluetooth" : "unknown";
}
